import { DialogService } from 'aurelia-dialog';
import { autoinject, Container, PLATFORM } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { AsyncErrorHandler, ViewLoadingFader } from 'lib/ui';
import { MooringAttachmentType, MooringComponentType, MooringOfferDto } from 'models/mooring';
import { MooringOfferService, MooringService } from 'services/mooring';
import { AttachmentHandlerFactory, MooringDocumentsHandler } from 'services/attachments';
import './offer-view.scss';
import { MooringCreateOfferDialog } from 'features/mooring/offers/mooring-create-offer-dialog';
import { PubSub } from 'lib/event/PubSub';
import { saveAs } from 'file-saver';
import {
  MooringOfferStatus,
  MooringOfferStatusTranslation,
  MooringOfferVersionDto,
  TransferToAquacomDto,
  UpdateInAquacomDto,
  UpdateMooringOfferStatusDto,
} from 'models/mooring/MooringOffers';
import { Tab } from 'elements/tabs/MnTabs';
import { I18N } from 'aurelia-i18n';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { MooringProductionOrderService } from 'services/mooring/mooring-production-order-service';
import { ToastService } from 'services';
import { OfferViewContext } from './OfferViewContext';
import { CustomerErpSystemService } from 'services/customer-erp-system-service';
import { Models } from 'models/core';
import { CustomerNumberErpDialog } from 'components/dialogs/customer-number-erp/customer-number-erp-dialog';
import { ProductSource } from 'models';

@autoinject
@OfferViewContext.provideContext()
export class OfferView {
  protected offerId: number;
  protected offer: MooringOfferDto;
  protected versionOffer: MooringOfferVersionDto;

  protected isInReview = false;
  protected isViewingVersion = false;
  protected versionNumber: number;
  protected versionId: number;
  protected versionMooringId: number;

  protected copyOfferProgress = false;

  protected mooringMapBus = new BroadcastChannel('mooring_map_bus');

  protected tabsReady = false;
  protected tabs: Tab[];

  private context = OfferViewContext.createContext({
    offer: null,
    // offerReview: null,
    inReview: false,
    isLocked: false,
  });

  constructor(
    protected element: Element,
    protected container: Container,
    private mooringOfferService: MooringOfferService,
    private mooringService: MooringService,
    private toastService: ToastService,
    private dialogService: DialogService,
    private t: I18N,
    private pubSub: PubSub,
    private confirmation: ConfirmDialog,
    private mooringProductionOrderService: MooringProductionOrderService,
    private toast: ToastService,
    private router: Router,
    private customerErpSystemService: CustomerErpSystemService
  ) {
    pubSub.sub('entity:updated', () => {
      void this.getOffer();
    });
    pubSub.sub('entity-parent:updated', () => {
      void this.getOffer();
    });
    pubSub.sub('list-entity:updated', ({ name }) => {
      if (this.offer.AquacomSyncedAt) {
        void this.updateChangesSinceLastTransferToAquacom();
      }

      if (!name) return;
      if (Object.values(MooringComponentType).includes(name as MooringComponentType)) {
        this.mooringMapBus.postMessage({
          map: 'update',
        });
      }
    });
  }

  activate(params: { id: number; versionId?: string; mooringId?: string }) {
    this.offerId = params.id;

    // Check if we're viewing a specific version
    if (params.versionId && params.mooringId) {
      this.isViewingVersion = true;
      this.versionMooringId = parseInt(params.mooringId);
      this.versionId = parseInt(params.versionId);
    }
  }

  @ViewLoadingFader
  async attached() {
    await this.getOffer();

    if (this.isViewingVersion && this.versionId) {
      // Load the archived version as a full offer
      await this.loadVersion();
    }
  }

  @AsyncErrorHandler
  async loadVersion() {
    this.tabsReady = false;
    const { data: version } = await this.mooringOfferService.getVersion(this.offerId, this.versionId);

    if (version) {
      this.versionNumber = version.VersionNumber;
      this.versionOffer = version;

      // Load the current active offer for basic info (customer, dates, etc.)
      const { data: currentOffer } = await this.mooringOfferService.getOffer(this.offerId);
      this.offer = currentOffer;

      // Setup tabs with version's MooringId
      this.tabs = this.getTabs(currentOffer.HasOnlyFleet);
      for (const tab of this.tabs) {
        if (!tab.data) tab.data = {};
        tab.data = {
          mooringId: version.MooringId,
          offerId: this.offerId,
          activeReviewId: null,
        };
      }

      this.context.setState({ offer: this.offer });
      this.tabsReady = true;
      this.setReviewState();
    }
  }

  @AsyncErrorHandler
  async exportControlTable() {
    const blob = await this.mooringService.exportProductionExcelFile(this.offer.MooringId);
    saveAs(blob.blob, blob.fileName);
  }

  @AsyncErrorHandler
  async createProductionOrder() {
    await this.mooringProductionOrderService.createProductionOrder({
      offerId: this.offerId,
    });
    // TODO: Ask to go to order
    this.toast.showCreated();
  }

  @AsyncErrorHandler
  async getOffer() {
    this.tabsReady = false;
    const { data } = await this.mooringOfferService.getOffer(this.offerId);
    await this.getMooringOfferReview(data.ActiveReviewId);
    this.offer = data;

    this.tabs = this.getTabs(this.offer.HasOnlyFleet);

    // Use version's MooringId if viewing a version, otherwise use current MooringId
    const mooringIdToUse = this.isViewingVersion ? this.versionMooringId : data.MooringId;

    for (const tab of this.tabs) {
      if (!tab.data) tab.data = {};
      tab.data = {
        mooringId: mooringIdToUse,
        offerId: data.Id,
        activeReviewId: data.ActiveReviewId,
      };
    }

    this.context.setState({
      offer: this.offer,
    });
    this.tabsReady = true;

    this.setReviewState();
  }

  @AsyncErrorHandler
  async getMooringOfferReview(activeReviewId: number) {
    if (!activeReviewId) return;
    const { data: review } = await this.mooringOfferService.getReview(activeReviewId);
    this.context.setState({
      offerReview: review,
    });
  }

  protected get statuses() {
    if (!this.offer?.AvailableStatuses) return [];

    return this.offer.AvailableStatuses.map((statusId) => ({
      label: MooringOfferStatusTranslation[statusId],
      value: statusId.toString(),
    }));
  }

  protected openMap() {
    const target = `${location.origin}/#/mooring/${this.offer.MooringId}/map?noskeleton=true`;
    const w = open(target, 'Map', 'width=1000, height=1000');
    w.focus();
  }

  protected setReviewState() {
    if (!this.offer) return;

    const inReview = this.offer.Status === MooringOfferStatus.Review;
    const isLocked = inReview || this.offer.IsLocked;

    this.context.setState({ inReview, isLocked });
    this.isInReview = inReview;
  }

  @AsyncErrorHandler
  protected async exportNavision() {
    const hasCustomerNumber = await this.ensureCustomerNumber();
    if (!hasCustomerNumber) return;

    const b = await this.mooringOfferService.exportOfferNavision(this.offerId);
    saveAs(b.blob, b.fileName);
  }

  @AsyncErrorHandler
  async transferToFileArea() {
    const hasCustomerNumber = await this.ensureCustomerNumber();
    if (!hasCustomerNumber) return;

    await this.mooringOfferService.transferToFileArea(this.offerId);
    this.toastService.showSuccess('general.transferBOMToFileAreaSuccess');
  }

  private async ensureCustomerNumber(): Promise<boolean> {
    if (!this.offer?.CustomerId) return false;

    const existing = await this.customerErpSystemService.getAll(
      `?$filter=CustomerId eq ${this.offer.CustomerId} and ErpSystemId eq ${ProductSource.Navision}`
    );

    if (existing && existing.some((x) => !!x.ErpCustomerId)) {
      return true;
    }

    const dialog = this.dialogService.open({
      viewModel: CustomerNumberErpDialog,
      model: {},
    });
    const result = await dialog.whenClosed();
    if (result.wasCancelled) return false;

    const erpCustomerId = result.output;
    if (!erpCustomerId) return false;

    await this.customerErpSystemService.post({
      CustomerId: this.offer.CustomerId,
      ErpSystemId: ProductSource.Navision,
      ErpCustomerId: erpCustomerId,
    } as Models.CustomerErpSystem);
    return true;
  }

  @AsyncErrorHandler
  protected async sendForReview() {
    await this.mooringOfferService.sendForReview(this.offerId);
    await this.getOffer();
  }

  @AsyncErrorHandler
  protected async unlockOffer() {
    const confirmed = await this.confirmation.confirmYesCancel('mooring.offers.unlock', this.t.tr('mooring.offers.unlockConfirmation'));
    if (!confirmed) return;

    const { data } = await this.mooringOfferService.unlockOffer(this.offerId);
    this.toastService.showSuccess('mooring.offerUnlocked');

    // Navigate to the new version
    if (data && data.Id) {
      this.router.navigate(`/mooring/offers/${data.Id}`, {
        replace: true,
        trigger: true,
      });
      window.location.reload();
    }
  }

  @AsyncErrorHandler
  protected async changeOfferStatus(status: { label: string; value: MooringOfferStatus }) {
    const confirmed = await this.confirmation.confirmYesCancel(
      'mooring.changeOfferStatus',
      this.t.tr('mooring.changeOfferStatusConfirmation', {
        x: this.t.tr(status.label),
      })
    );
    if (!confirmed) return;
    const dto = new UpdateMooringOfferStatusDto();
    dto.Status = status.value;
    await this.mooringOfferService.updateOfferStatus(this.offerId, dto);

    this.pubSub.publish('entity:updated', null);
  }

  protected canDeactivate() {}

  protected detached() {
    this.pubSub.unsub();
  }

  @AsyncErrorHandler
  protected async transferToAquacom() {
    if (this.offer.AquacomMooringId) {
      const updateInAquacom: UpdateInAquacomDto = {
        OfferId: this.offerId,
        CustomerId: this.offer.CustomerId,
        LocalityId: this.offer.SiteId,
        MooringAquacomId: this.offer.AquacomMooringId,
      };
      const response = await this.mooringOfferService.updateOfferInAquacom(updateInAquacom);
      if (response.status === 200) {
        this.pubSub.publish('entity:updated', null);
        this.toastService.showSuccess('general.offerUpdatedInAquacom');
      }
    } else {
      const transferToAquacom: TransferToAquacomDto = {
        OfferId: this.offerId,
        CustomerId: this.offer.CustomerId,
        LocalityId: this.offer.SiteId,
      };
      const response = await this.mooringOfferService.transferOfferToAquacom(transferToAquacom);
      if (response.status === 200) {
        this.pubSub.publish('entity:updated', null);
        this.toastService.showSuccess('general.transferedToAquacom');
      }
    }
  }

  editDetails() {
    void this.dialogService.open({
      viewModel: MooringCreateOfferDialog,
      model: {
        id: this.offerId,
      },
    });
  }

  protected openVersionsDialog() {
    void this.dialogService.open({
      viewModel: PLATFORM.moduleName('views/mooring/mooring-offers/versions-dialog'),
      model: {
        offerId: this.offerId,
      },
    });
  }

  @AsyncErrorHandler
  async updateChangesSinceLastTransferToAquacom() {
    const { data } = await this.mooringOfferService.getOffer(this.offerId);
    this.offer.ChangesSinceLastTransferToAquacom = data.ChangesSinceLastTransferToAquacom;
  }

  @AsyncErrorHandler
  async copyOffer() {
    const confirmed = await this.confirmation.confirmYesCancel(
      'general.copyOffer',
      this.t.tr('mooring.copyOfferConfirmation', { x: this.offer.Title })
    );
    if (!confirmed) return;

    this.copyOfferProgress = true;
    await this.mooringOfferService.copyOffer(this.offerId);
    this.toastService.showSuccess('mooring.copyOfferCreated');
    this.copyOfferProgress = false;
  }

  private getTabs(hasOnlyFleet: boolean) {
    const review = this.context.getState().offerReview;

    return hasOnlyFleet
      ? [
          {
            name: 'overview',
            title: this.t.tr('mooring.offer'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/overview/tab'),
            data: {},
          },
          {
            name: 'fleet-anchor-lines',
            title: this.t.tr('mooring.fleetAnchorLine_plural'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/fleet-anchor-lines/tab'),
            guard: true,
            data: {},
            icons: { right: review?.FleetAnchorLinesReviewedAt ? 'check' : '' },
          },
          {
            name: 'review',
            title: this.t.tr('mooring.review'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/review/tab'),
            data: {},
          },
          {
            name: 'bom',
            title: 'BOM',
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/bom/tab'),
            data: {},
          },
        ]
      : [
          {
            name: 'overview',
            title: this.t.tr('mooring.offer'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/overview/tab'),
            data: {},
          },
          {
            name: 'anchor-lines',
            title: this.t.tr('mooring.anchorLine_plural'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/anchor-lines/tab'),
            guard: true,
            data: {},
            icons: { right: review?.AnchorLinesReviewedAt ? 'check' : '' },
          },
          {
            name: 'grid-lines',
            title: this.t.tr('mooring.gridLine_plural'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/grid-lines/tab'),
            guard: true,
            data: {},
            icons: { right: review?.GridLinesReviewedAt ? 'check' : '' },
          },
          {
            name: 'buoys',
            title: this.t.tr('mooring.buoy_plural'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/buoys/tab'),
            guard: true,
            data: {},
            icons: { right: review?.BuoysReviewedAt ? 'check' : '' },
          },
          {
            name: 'cages',
            title: this.t.tr('mooring.floater_plural'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/cages/tab'),
            guard: true,
            data: {},
            icons: { right: review?.CagesReviewedAt ? 'check' : '' },
          },
          {
            name: 'bridles',
            title: this.t.tr('mooring.bridle_plural'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/bridles/tab'),
            guard: true,
            data: {},
            icons: { right: review?.BridlesReviewedAt ? 'check' : '' },
          },
          {
            name: 'fleet-anchor-lines',
            title: this.t.tr('mooring.fleetAnchorLine_plural'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/fleet-anchor-lines/tab'),
            guard: true,
            data: {},
            icons: { right: review?.FleetAnchorLinesReviewedAt ? 'check' : '' },
          },
          {
            name: 'review',
            title: this.t.tr('mooring.review'),
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/review/tab'),
            data: {},
          },
          {
            name: 'bom',
            title: 'BOM',
            view: PLATFORM.moduleName('views/mooring/mooring-offers/offer/bom/tab'),
            data: {},
          },
        ];
  }
}
